export function validateForm(pages) {
  const result = {
    totalRequired: 0,
    filledRequired: 0,
    missingRequired: 0,
    missingFields: [],
    isValid: true,
  };

  const isEmpty = value => {
    if (value === null || value === undefined) return true;

    if (typeof value === "string") return value.trim() === "";

    if (typeof value === "boolean") return value === false;

    if (Array.isArray(value)) return value.length === 0;

    if (typeof value === "object") return Object.keys(value).length === 0;

    return false;
  };

  // FIX 6: richText content like "<p></p>" or "<p><br></p>" looks non-empty to
  // a plain string check but represents an empty editor state.
  const isRichTextEmpty = value => {
    if (!value || typeof value !== "string") return true;
    const stripped = value.replace(/<[^>]*>/g, "").trim();
    return stripped === "";
  };

  const checkRequired = (el, label, type, path) => {
    if (!el?.props?.isRequired) return;

    result.totalRequired++;

    let filled = true;

    switch (type) {
      case "checkElement":
        filled = el.props.content === true;
        break;

      case "checkGroupElement":
        filled = el.props.fields?.some(f => f.value === true);
        break;

      case "likertScaleElement":
        filled = el.props.questions?.every(q => q.response != null);
        break;

      case "tableElement":
        filled = Object.keys(el.props.data || {}).length > 0;
        break;

      // FIX 1: attachmentElement was reading el.content — must be el.props.content
      case "attachmentElement":
        filled = Array.isArray(el.props.content) && el.props.content.length > 0;
        break;

      // FIX 5: Added dedicated imageElement case — checks for a valid uploaded file
      case "imageElement":
        filled =
          el.props.content != null &&
          typeof el.props.content === "object" &&
          !isEmpty(el.props.content);
        break;

      // If the parent profileElement is required, ALL sub-fields must have
      // content regardless of their own isRequired flag. Report each empty
      // sub-field individually so the label shown is the sub-field's own label.
      case "profileElement": {
        const emptyFields =
          el.props.fields?.filter(f => isEmpty(f.fieldData?.props?.content)) ||
          [];
        if (emptyFields.length > 0) {
          // Undo the generic totalRequired++ done before the switch, then
          // count each sub-field as its own required entry instead.
          result.totalRequired--;
          emptyFields.forEach((f, idx) => {
            const subLabel = f.fieldData?.props?.label || "Field " + (idx + 1);
            result.totalRequired++;
            result.missingRequired++;
            result.missingFields.push({
              id: el.id + "_" + idx,
              label: subLabel,
              type: "profileField",
              path,
            });
          });
          // Also count the filled sub-fields toward totalRequired
          const filledCount =
            (el.props.fields?.length || 0) - emptyFields.length;
          result.totalRequired += filledCount;
          result.filledRequired += filledCount;
          filled = false; // prevent the default filledRequired++ below
        }
        break;
      }

      case "inlineDocumentElement":
        filled = Object.values(el.props.content?.fields || {}).every(
          f => !f.required || !isEmpty(f.value)
        );
        break;

      case "dateElement":
      case "timeElement":
      case "dateAndTimeElement":
      case "dropdownSelectElement":
      case "emailElement":
      case "inputElement":
      case "longTextElement":
      case "radioElement":
        filled = !isEmpty(el.props.content);
        break;

      // FIX 6: richTextElement needs HTML-aware empty check
      case "richTextElement":
        filled = !isRichTextEmpty(el.props.content);
        break;

      default:
        filled = !isEmpty(el.props?.content);
    }

    if (filled) {
      result.filledRequired++;
    } else {
      result.missingRequired++;
      result.missingFields.push({
        id: el.id,
        label: label || el.props?.label || "Required field",
        type,
        path,
      });
    }
  };

  const walk = (elements, path = "root") => {
    if (!elements) return;

    for (const el of elements) {
      const currentPath = `${path} > ${el.type}:${el.id}`;

      // Validate this element
      checkRequired(el, el.props?.label, el.type, currentPath);

      // Recurse children (columns, containers, pages)
      // FIX 3: Removed the redundant `if (el.type === "columnElement")` block
      // below — the general children walk here already handles columnElement,
      // and the duplicate block was causing every column child to be validated twice.
      if (el.children?.length) {
        walk(el.children, currentPath);
      }

      if (el.type === "signatoryElement") {
        el.props.fields?.forEach((f, i) => {
          ["name", "date", "signature"].forEach(key => {
            const field = f[key];
            if (field?.props?.isRequired) {
              result.totalRequired++;
              const filled = !isEmpty(field.props.content);

              if (filled) result.filledRequired++;
              else {
                result.missingRequired++;
                result.missingFields.push({
                  id: `${el.id}_${i}_${key}`,
                  label: field.props.label || key,
                  type: "signatoryField",
                  path: currentPath,
                });
              }
            }
          });
        });
      }

      // FIX 4: profileElement sub-fields are counted here. checkRequired above
      // handles the top-level isRequired flag; this block only counts individual
      // sub-fields that have their own isRequired set to true.
      if (el.type === "profileElement") {
        el.props.fields?.forEach((f, i) => {
          const p = f.fieldData?.props;
          if (p?.isRequired) {
            result.totalRequired++;
            const filled = !isEmpty(p.content);

            if (filled) result.filledRequired++;
            else {
              result.missingRequired++;
              result.missingFields.push({
                id: `${el.id}_${i}`,
                label: p.label,
                type: "profileField",
                path: currentPath,
              });
            }
          }
        });
      }
    }
  };

  walk(pages);

  result.isValid = result.missingRequired === 0;

  return result;
}
